/**
 * Simple analytics loader + event helpers.
 * - Loads Google Analytics (GA4) if `ga4_id` is set
 * - Loads Meta Pixel if `meta_pixel_id` is set
 * - Provides `track()` for e-commerce events (view_item, add_to_cart, purchase, etc.)
 * Both scripts respect the same cookie-consent gate: only load once the user has accepted cookies.
 */

let LOADED = { ga: false, pixel: false };
let PENDING = { ga: null, pixel: null };

function _hasConsent() {
  try {
    return localStorage.getItem("cookie_consent") === "all";
  } catch {
    return false;
  }
}

export function initAnalytics({ ga4_id, meta_pixel_id }) {
  if (!_hasConsent()) return;

  if (ga4_id && !LOADED.ga) {
    // Inject GA4 gtag.js
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${ga4_id}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", ga4_id, { send_page_view: true });
    LOADED.ga = true;
  }

  if (meta_pixel_id && !LOADED.pixel) {
    // Meta Pixel snippet (official from developers.facebook.com)
    /* eslint-disable */
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
      t = b.createElement(e); t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    /* eslint-enable */
    window.fbq("init", meta_pixel_id);
    window.fbq("track", "PageView");
    LOADED.pixel = true;
  }
}

/** Track a page view (call on route change). */
export function trackPageView(path) {
  if (!_hasConsent()) return;
  if (window.gtag) window.gtag("event", "page_view", { page_path: path });
  if (window.fbq) window.fbq("track", "PageView");
}

/** Track a named event with GA + Pixel — maps to reasonable defaults. */
export function track(eventName, params = {}) {
  if (!_hasConsent()) return;
  const GA_MAP = {
    view_item: "view_item",
    add_to_cart: "add_to_cart",
    begin_checkout: "begin_checkout",
    purchase: "purchase",
    newsletter_signup: "generate_lead",
  };
  const PIXEL_MAP = {
    view_item: "ViewContent",
    add_to_cart: "AddToCart",
    begin_checkout: "InitiateCheckout",
    purchase: "Purchase",
    newsletter_signup: "Lead",
  };
  const gaEvent = GA_MAP[eventName] || eventName;
  const pxEvent = PIXEL_MAP[eventName];

  if (window.gtag) {
    // Pass GA-friendly params: currency, value, items[]
    window.gtag("event", gaEvent, params);
  }
  if (window.fbq && pxEvent) {
    // Pixel wants currency + value + content_ids
    const pxParams = {};
    if (params.currency) pxParams.currency = params.currency;
    if (params.value != null) pxParams.value = params.value;
    if (params.items) {
      pxParams.content_ids = params.items.map((i) => i.item_id || i.slug).filter(Boolean);
      pxParams.contents = params.items.map((i) => ({
        id: i.item_id || i.slug, quantity: i.quantity || 1,
      }));
    }
    window.fbq("track", pxEvent, pxParams);
  }
}


// ---------------------------------------------------------------------------
// First-party (cookieless-safe) analytics — sent to our own backend so the
// admin dashboard can show traffic + clicks even for visitors who declined
// GA/Meta tracking cookies.
// ---------------------------------------------------------------------------
const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
const SESSION_KEY = "mdb_session_id_v1";

function _sessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = (crypto?.randomUUID?.() || `s${Date.now()}${Math.random().toString(36).slice(2, 8)}`).replace(/-/g, "");
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch (err) {
    console.warn("analytics: sessionStorage unavailable", err);
    return "anon";
  }
}

function _beacon(url, body) {
  try {
    const json = JSON.stringify(body);
    // Prefer sendBeacon so navigation-away clicks still record
    if (navigator.sendBeacon) {
      const blob = new Blob([json], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    }
    fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: json, keepalive: true })
      .catch((err) => console.warn("analytics beacon failed", err));
  } catch (err) {
    console.warn("analytics beacon threw", err);
  }
}

/** Fire-and-forget: record a page view to our own backend. */
export function pingPageView(path) {
  if (!path) return;
  _beacon(`${API_BASE}/analytics/pageview`, {
    path,
    referrer: document.referrer || "",
    session_id: _sessionId(),
  });
}

/** Fire-and-forget: record a named click event. */
export function pingClick(name, meta) {
  if (!name) return;
  _beacon(`${API_BASE}/analytics/click`, {
    name,
    path: window.location.pathname,
    session_id: _sessionId(),
    meta: meta || undefined,
  });
}
